import React, { useEffect, useState, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import brLocale from "date-fns/locale/pt-BR";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { TextField as MuiTextField } from "@mui/material";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Popover from "@material-ui/core/Popover";
import MuiTooltip from "@material-ui/core/Tooltip";
import DateRangeIcon from "@material-ui/icons/DateRange";
import api from "../../services/api";
import { format } from "date-fns";
import { toast } from "sonner";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const getLastDayOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const getFirstDayOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

export const ChartsDate = () => {
  const [initialDate, setInitialDate] = useState(
    getFirstDayOfMonth(new Date())
  );
  const [finalDate, setFinalDate] = useState(getLastDayOfMonth(new Date()));
  const [ticketsData, setTicketsData] = useState({ data: [], count: 0 });
  const [dateAnchor, setDateAnchor] = useState(null);
  const chartRef = useRef(null);

  useEffect(() => {
    handleGetTicketsInformation();
  }, [initialDate, finalDate]);

  const handleGetTicketsInformation = async () => {
    try {
      const { data } = await api.get(
        `/dashboard/total-atendimentos?initialDate=${format(
          initialDate,
          "yyyy-MM-dd"
        )}&finalDate=${format(finalDate, "yyyy-MM-dd")}`
      );
      setTicketsData(data);
    } catch (error) {
      toast.error("Erro ao buscar informações dos tickets");
    }
  };

  const createGradient = (ctx, chartArea) => {
    if (!chartArea) return "rgba(59, 130, 246, 0.3)";
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, "rgba(59, 130, 246, 0.35)");
    gradient.addColorStop(1, "rgba(59, 130, 246, 0.02)");
    return gradient;
  };

  const dataCharts = {
    labels:
      ticketsData?.data.length > 0
        ? ticketsData.data.map((item) =>
            item.hasOwnProperty("horario")
              ? `${item.horario}h`
              : item.data
          )
        : [],
    datasets: [
      {
        label: "Total",
        data:
          ticketsData?.data.length > 0
            ? ticketsData.data.map((item) => item.total)
            : [],
        borderColor: "#3B82F6",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          return createGradient(ctx, chartArea);
        },
        pointBackgroundColor: "#3B82F6",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2.5,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1500,
      easing: "easeInOutQuart",
    },
    plugins: {
      legend: { display: false },
      title: { display: false },
      datalabels: {
        display: true,
        anchor: "end",
        align: "end",
        color: "#64748B",
        font: { family: "Inter", size: 11, weight: "bold" },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { family: "Inter", size: 11 }, color: "#64748B" },
      },
      y: {
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { font: { family: "Inter", size: 11 }, color: "#94A3B8" },
        beginAtZero: true,
      },
    },
  };

  return (
    <>
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Typography
            component="h2"
            variant="h6"
            style={{
              fontFamily: '"Inter", "Poppins", sans-serif',
              fontWeight: 700,
              fontSize: 15,
              color: "#1E293B",
            }}
          >
            Total de atendimentos
          </Typography>
          <span
            style={{
              fontFamily: '"Inter", "Poppins", sans-serif',
              fontSize: 20,
              fontWeight: 700,
              color: "#35af9a",
            }}
          >
            {ticketsData?.count || 0}
          </span>
        </Box>

        <MuiTooltip title="Período">
          <IconButton
            size="small"
            onClick={(e) => setDateAnchor(e.currentTarget)}
            style={{
              borderRadius: 8,
              backgroundColor: "#F1F5F9",
              width: 32,
              height: 32,
            }}
          >
            <DateRangeIcon style={{ fontSize: 18, color: "#64748B" }} />
          </IconButton>
        </MuiTooltip>
      </Box>

      <Popover
        open={Boolean(dateAnchor)}
        anchorEl={dateAnchor}
        onClose={() => setDateAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            borderRadius: 12,
            minWidth: 240,
          },
        }}
      >
        <Typography
          style={{
            fontFamily: '"Inter", "Poppins", sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: "#1E293B",
            marginBottom: 4,
          }}
        >
          Período
        </Typography>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={brLocale}
        >
          <DatePicker
            value={initialDate}
            onChange={(newValue) => setInitialDate(newValue)}
            label="Data inicial"
            renderInput={(params) => (
              <MuiTextField fullWidth {...params} size="small" />
            )}
          />
        </LocalizationProvider>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={brLocale}
        >
          <DatePicker
            value={finalDate}
            onChange={(newValue) => setFinalDate(newValue)}
            label="Data final"
            renderInput={(params) => (
              <MuiTextField fullWidth {...params} size="small" />
            )}
          />
        </LocalizationProvider>
      </Popover>

      <div style={{ height: 260 }}>
        <Line ref={chartRef} options={options} data={dataCharts} />
      </div>
    </>
  );
};
